import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState, useCallback } from "react";
import { AppShell } from "@/components/app-shell";
import { supabase } from "@/integrations/supabase/client";
import { fetchFeed } from "@/lib/posts";
import { PostCard, type FeedPost, type PostMedia } from "@/components/post-card";
import { MediaLightbox } from "@/components/media-lightbox";
import { EditProfileDialog } from "@/components/edit-profile-dialog";
import { ProfileGalleryDialog, type ProfileMediaItem } from "@/components/profile-gallery-dialog";
import { ProfilePhotoDialog } from "@/components/profile-photo-dialog";
import { JourneyTimeline, buildJourney } from "@/components/journey-timeline";
import { ImpactRadar } from "@/components/impact-radar";
import { computeCivicScore, fromRpcRow, type CivicScore, type CivicInputs } from "@/lib/civic-score";
import { ScoreBreakdown } from "@/components/score-breakdown";
import { ScoreWindows } from "@/components/score-windows";

import { syncCivicProgress } from "@/lib/civic-progress";
import { BadgeGrid, buildBadges } from "@/components/badge-grid";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Calendar, MapPin, Loader2, Globe, Share2, MessageSquarePlus,
  ShieldCheck, ThumbsUp, Bookmark, Flag, Users as UsersIcon, MessageCircle,
  FileText, Trophy, CheckCircle2, Eye, Image as ImageIcon, Video as VideoIcon,
  Play, BarChart3, Briefcase, Heart, Settings2, Camera,
} from "lucide-react";
import { format, formatDistanceToNow } from "date-fns";
import { useAuth } from "@/lib/auth-context";
import { toast } from "sonner";
import { getOrCreateDirectConversation } from "@/lib/messaging";
import { cn } from "@/lib/utils";
import { AccountBadge, ACCOUNT_META, isOfficial, type AccountType } from "@/components/account-badge";
import { UserAvatar } from "@/components/user-avatar";
import { FollowButton } from "@/components/follow-button";
import { motion } from "framer-motion";
import { OfficialImpactSection } from "@/components/official-impact-section";
import { PUBLIC_SERVICE_ACCOUNT_TYPES } from "@/lib/impact-projects";

export const Route = createFileRoute("/profile/$username")({
  component: ProfilePage,
});

interface ProfileData {
  id: string; username: string; full_name: string | null; bio: string | null;
  location: string | null; avatar_url: string | null; cover_url: string | null; website: string | null;
  city: string | null; country: string | null;
  reputation_points: number; civic_score: number; impact_score: number;
  xp: number; level: number; streak_days: number;
  verified: boolean; verification_type: string | null;
  account_type: AccountType | null;
  office: string | null; jurisdiction: string | null; agency: string | null; official_website: string | null;
  created_at: string;
  interests: string[] | null;
}

const DRIVE_DOTS = ["bg-foreground", "bg-foreground/80", "bg-foreground/65", "bg-foreground/50", "bg-foreground/35"];

interface Metrics {
  posts: number;
  discussions: number;
  questions: number;
  solutions: number;
  acceptedSolutions: number;
  comments: number;
  communitiesJoined: number;
  communitiesCreated: number;
  projectsJoined: number;
  projectsCreated: number;
  verificationsGiven: number;
  supportReceived: number;
  bookmarksReceived: number;
  issuesReported: number;
  issuesResolved: number;
  followers: number;
  following: number;
  totalReach: number;
}

function trustScore(p: ProfileData, m: Metrics) {
  const base = p.verified ? 40 : 10;
  return Math.min(100, base + Math.min(30, m.acceptedSolutions * 3) + Math.min(20, m.verificationsGiven) + Math.min(10, Math.floor(m.supportReceived / 5)));
}

const nf = (n: number) => n >= 1000 ? `${(n / 1000).toFixed(n >= 10000 ? 0 : 1)}K` : `${n}`;

function ProfilePage() {
  const { username } = Route.useParams();
  const { user } = useAuth();
  const [profile, setProfile] = useState<ProfileData | null>(null);
  const [posts, setPosts] = useState<FeedPost[]>([]);
  const [metrics, setMetrics] = useState<Metrics | null>(null);
  const [score, setScore] = useState<CivicScore | null>(null);
  const [achievements, setAchievements] = useState<any[]>([]);
  const [activity, setActivity] = useState<any[]>([]);
  const [replies, setReplies] = useState<any[]>([]);
  const [communities, setCommunities] = useState<any[]>([]);
  const [projects, setProjects] = useState<any[]>([]);
  const [isFollowing, setIsFollowing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [gallery, setGallery] = useState<ProfileMediaItem[]>([]);
  const [editOpen, setEditOpen] = useState(false);
  const [photoOpen, setPhotoOpen] = useState(false);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [messaging, setMessaging] = useState(false);
  const navigate = useNavigate();

  const isMe = user?.id === profile?.id;

  const loadGallery = useCallback(async (uid: string) => {
    const { data } = await supabase
      .from("profile_media" as any)
      .select("*")
      .eq("user_id", uid)
      .order("position", { ascending: true });
    setGallery((data ?? []) as any);
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      const PROFILE_COLS = "id, username, full_name, bio, location, avatar_url, cover_url, website, city, country, reputation_points, civic_score, impact_score, xp, level, streak_days, verified, verification_type, account_type, office, jurisdiction, agency, official_website, created_at, interests";
      let { data: prof } = await supabase
        .from("profiles")
        .select(PROFILE_COLS)
        .eq("username", username)
        .maybeSingle();
      if (!prof && user) {
        // Brand-new account whose profile row hasn't been created yet — build it, then retry.
        const { data: handle } = await supabase.rpc("ensure_my_profile" as never);
        const mine = (handle as unknown as string) ?? null;
        const res = await supabase
          .from("profiles")
          .select(PROFILE_COLS)
          .eq(mine && mine !== username ? "id" : "username", mine && mine !== username ? user.id : username)
          .maybeSingle();
        prof = res.data;
      }
      if (!prof) { setLoading(false); return; }

      const p = prof as ProfileData;
      setProfile(p);

      const uid = p.id;
      const [
        feed, postsAll, commentsCnt, accSol, verifsGiven, supportRecv, bookmarksRecv,
        commJoinedRows, commCreatedCnt, projJoinedRows, projCreatedRows,
        followersRows, followingRows, achs, acts, isFollowingRes, replyRows,
      ] = await Promise.all([
        fetchFeed({ authorId: uid, userId: user?.id, sort: "latest", limit: 30 }),
        supabase.from("posts").select("id, post_type, status, accepted_solution_id", { count: "exact" }).eq("author_id", uid),
        supabase.from("comments").select("id", { count: "exact", head: true }).eq("author_id", uid),
        supabase.from("comments").select("id", { count: "exact", head: true }).eq("author_id", uid).eq("is_solution", true),
        supabase.from("reactions").select("id", { count: "exact", head: true }).eq("user_id", uid).eq("reaction", "verify" as any),
        supabase.from("posts").select("upvotes, view_count").eq("author_id", uid),
        supabase.from("saves").select("post_id, posts!inner(author_id)").eq("posts.author_id", uid),
        supabase.from("community_members").select("community_id, role, communities(id, name, slug, cover_url, member_count)").eq("user_id", uid),
        supabase.from("communities").select("id", { count: "exact", head: true }).eq("created_by", uid),
        supabase.from("project_members").select("project_id, projects(id, title, status, cover_url, category)").eq("user_id", uid),
        supabase.from("projects").select("id, title, status, cover_url, category, volunteer_count").eq("created_by", uid).limit(20),
        supabase.from("follows").select("follower_id", { count: "exact", head: true }).eq("following_id", uid),
        supabase.from("follows").select("following_id", { count: "exact", head: true }).eq("follower_id", uid),
        supabase.from("user_achievements").select("earned_at, achievements(id, slug, name, description, icon, tier, xp_reward)").eq("user_id", uid).order("earned_at", { ascending: false }),
        supabase.from("activity_log").select("id, action, entity_type, entity_id, metadata, created_at").eq("user_id", uid).order("created_at", { ascending: false }).limit(40),
        user ? supabase.from("follows").select("follower_id").eq("follower_id", user.id).eq("following_id", uid).maybeSingle() : Promise.resolve({ data: null } as any),
        supabase.from("comments").select("id, content, created_at, is_solution, post_id, posts(id, title, location_name)").eq("author_id", uid).order("created_at", { ascending: false }).limit(30),
      ]);

      const postsList = (postsAll.data ?? []) as any[];
      const m: Metrics = {
        posts: postsAll.count ?? postsList.length,
        discussions: postsList.filter(p => p.post_type === "discussion").length,
        questions: postsList.filter(p => p.post_type === "question").length,
        solutions: accSol.count ?? 0,
        acceptedSolutions: accSol.count ?? 0,
        comments: commentsCnt.count ?? 0,
        communitiesJoined: commJoinedRows.data?.length ?? 0,
        communitiesCreated: commCreatedCnt.count ?? 0,
        projectsJoined: projJoinedRows.data?.length ?? 0,
        projectsCreated: projCreatedRows.data?.length ?? 0,
        verificationsGiven: verifsGiven.count ?? 0,
        supportReceived: (supportRecv.data ?? []).reduce((a: number, r: any) => a + (r.upvotes ?? 0), 0),
        bookmarksReceived: bookmarksRecv.data?.length ?? 0,
        issuesReported: postsList.filter(p => p.post_type === "issue" || p.post_type === "emergency").length,
        issuesResolved: postsList.filter(p => p.status === "resolved" || p.accepted_solution_id).length,
        followers: followersRows.count ?? 0,
        following: followingRows.count ?? 0,
        totalReach: (supportRecv.data ?? []).reduce((a: number, r: any) => a + (r.view_count ?? 0), 0),
      };

      setPosts(feed);
      setMetrics(m);

      // Civic score / radar / level from real events (local mirror first, then
      // the authoritative server calculation which also persists the values).
      setScore(computeCivicScore(m));
      if (user) {
        const { data: rpc } = await supabase.rpc("recompute_civic_score" as never, { _user_id: uid } as never);
        const row = Array.isArray(rpc) ? rpc[0] : rpc;
        const fromServer = fromRpcRow(row);
        if (user.id === uid) void syncCivicProgress();
        if (fromServer) {
          setScore(fromServer);
          setProfile(prev => prev ? { ...prev, civic_score: fromServer.civic, impact_score: fromServer.impact, xp: fromServer.xp, level: fromServer.level } : prev);
        }
      }
      setAchievements(achs.data ?? []);
      setActivity(acts.data ?? []);
      setReplies(replyRows.data ?? []);
      setCommunities((commJoinedRows.data ?? []).map((r: any) => ({ ...r.communities, role: r.role })).filter((c: any) => c?.id));
      setProjects([
        ...(projCreatedRows.data ?? []).map((p: any) => ({ ...p, owned: true })),
        ...((projJoinedRows.data ?? []).map((r: any) => r.projects).filter(Boolean)),
      ]);
      setIsFollowing(!!isFollowingRes.data);

      await loadGallery(uid);
      setLoading(false);
    })();
  }, [username, user?.id, loadGallery]);

  const share = async () => {
    const url = window.location.href;
    try {
      if (navigator.share) await navigator.share({ url, title: profile?.full_name || profile?.username });
      else { await navigator.clipboard.writeText(url); toast.success("Profile link copied"); }
    } catch {}
  };

  const startMessage = async () => {
    if (!profile) return;
    if (!user) {
      navigate({ to: "/signin", search: { redirect: window.location.pathname } as any });
      return;
    }
    setMessaging(true);
    try {
      const id = await getOrCreateDirectConversation(user.id, profile.id);
      navigate({ to: "/messages/$id", params: { id } });
    } catch (e: any) {
      toast.error(e?.message ?? "Could not open this conversation.");
    } finally {
      setMessaging(false);
    }
  };

  const allMedia = useMemo(() => {
    const arr: (PostMedia & { postId: string })[] = [];
    for (const p of posts) {
      if (p.media && p.media.length > 0) {
        [...p.media].sort((a, b) => a.position - b.position).forEach(m => arr.push({ ...m, postId: p.id }));
      } else if (p.image_url) {
        arr.push({ id: p.id + "-img", url: p.image_url, media_type: "image", position: 0, postId: p.id });
      }
    }
    return arr;
  }, [posts]);
  const galleryAsMedia = useMemo<PostMedia[]>(() => gallery.map(g => ({
    id: `gal-${g.id}`, url: g.url, media_type: g.media_type as any, position: g.position,
  })), [gallery]);
  const mediaItems = useMemo(() => [...galleryAsMedia, ...allMedia], [allMedia, galleryAsMedia]);
  const [lightbox, setLightbox] = useState<{ open: boolean; start: number; items: PostMedia[] }>({ open: false, start: 0, items: [] });

  if (loading) return <AppShell allowAnonymous><div className="flex justify-center py-20"><Loader2 className="h-5 w-5 animate-spin" /></div></AppShell>;
  if (!profile || !metrics) return <AppShell allowAnonymous><div className="py-20 text-center text-muted-foreground">User not found.</div></AppShell>;

  const trust = trustScore(profile, metrics);
  const liveScore = score ?? computeCivicScore(metrics);
  const civicInputs: CivicInputs = {
    issuesReported: metrics.issuesReported,
    issuesResolved: metrics.issuesResolved,
    comments: metrics.comments,
    acceptedSolutions: metrics.acceptedSolutions,
    verificationsGiven: metrics.verificationsGiven,
    supportReceived: metrics.supportReceived,
    totalReach: metrics.totalReach,
    communitiesJoined: metrics.communitiesJoined,
    projectsJoined: metrics.projectsJoined,
    projectsCreated: metrics.projectsCreated,
    followers: metrics.followers,
  };
  const badgeList = buildBadges({
    issuesReported: metrics.issuesReported,
    verificationsGiven: metrics.verificationsGiven,
    followers: metrics.followers,
    acceptedSolutions: metrics.acceptedSolutions,
    spaces: metrics.communitiesJoined,
    issuesResolved: metrics.issuesResolved,
  });
  const initials = (profile.full_name?.[0] ?? profile.username[0] ?? "U").toUpperCase();
  const locText = profile.city && profile.country ? `${profile.city}, ${profile.country}` : profile.location;
  const displayName = profile.full_name || profile.username;
  const reports = posts.filter(p => p.post_type === "issue" || (p as any).post_type === "emergency");
  const showsImpactFlag =
    !!profile.verified &&
    (PUBLIC_SERVICE_ACCOUNT_TYPES as readonly string[]).includes(String(profile.account_type));
  const acceptedReplies = replies.filter((r: any) => r.is_solution);
  // Only surface a tab when it actually has something in it.
  const tabsShown: [string, string][] = [
    ["posts", "Posts"],
    ...(replies.length ? ([["replies", "Replies"]] as [string, string][]) : []),
    ...(reports.length ? ([["reports", "Reports"]] as [string, string][]) : []),
    ...(acceptedReplies.length ? ([["solutions", "Solutions"]] as [string, string][]) : []),
    ...(showsImpactFlag ? ([["impact", "Impact"]] as [string, string][]) : []),
    ...(mediaItems.length || isMe ? ([["media", "Media"]] as [string, string][]) : []),
    ...(communities.length ? ([["communities", "Communities"]] as [string, string][]) : []),
    ["about", "About"],
  ];
  const showsImpact = showsImpactFlag;

  return (
    <AppShell allowAnonymous rightRail={<ProfileRail profile={profile} metrics={metrics} communities={communities} projects={projects} />}>
      <div className="-mx-4 sm:-mx-6">
        {/* Cover */}
        <div
          className="h-36 w-full bg-muted sm:h-48"
          style={profile.cover_url ? { backgroundImage: `url(${profile.cover_url})`, backgroundSize: "cover", backgroundPosition: "center" } : undefined}
        />

        {/* Header */}
        <div className="px-4 sm:px-6">
          <div className="grid grid-cols-[auto_minmax(0,1fr)] items-start gap-3">
            <motion.div
              initial={{ opacity: 0, scale: 0.94 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.32, ease: [0.22, 0.61, 0.36, 1] }}
              className="-mt-12 rounded-full sm:-mt-16"
            >
              <div className="relative rounded-full border-4 border-background shadow-sm">
                <UserAvatar
                  name={profile.full_name}
                  username={profile.username}
                  avatarUrl={profile.avatar_url}
                  size={128}
                  className="h-[86px] w-[86px] text-2xl sm:h-[118px] sm:w-[118px]"
                />
                {isMe && (
                  <button
                    type="button"
                    onClick={() => setPhotoOpen(true)}
                    aria-label="Change profile photo"
                    className="absolute bottom-0 right-0 grid h-9 w-9 place-items-center rounded-full bg-foreground text-background shadow-sm transition-transform hover:scale-105"
                  >
                    <Camera className="h-4 w-4" />
                  </button>
                )}
              </div>
            </motion.div>

            <div className="flex min-w-0 flex-nowrap items-center justify-end gap-1.5 pt-3 sm:gap-2">
              <Button variant="outline" size="sm" className="h-11 w-11 shrink-0 rounded-full p-0" onClick={share} aria-label="Share profile">
                <Share2 className="h-4 w-4" />
              </Button>
              {!isMe && (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-11 w-11 shrink-0 rounded-full p-0"
                    onClick={startMessage}
                    disabled={messaging}
                    aria-label={`Message ${displayName}`}
                    title={`Message ${displayName}`}
                  >
                    {messaging ? <Loader2 className="h-4 w-4 animate-spin" /> : <MessageSquarePlus className="h-4 w-4" />}
                  </Button>
                  <div className="min-w-0 shrink-0">
                    <FollowButton
                      targetId={profile.id}
                      initialFollowing={isFollowing}
                      size="sm"
                      onChange={(f) => {
                        setIsFollowing(f);
                        setMetrics((m) => (m ? { ...m, followers: Math.max(0, m.followers + (f ? 1 : -1)) } : m));
                      }}
                    />
                  </div>
                </>
              )}
              {isMe && (
                <>
                  <Button variant="outline" size="sm" className="h-11 w-11 shrink-0 rounded-full p-0" onClick={() => setGalleryOpen(true)} aria-label="Manage gallery">
                    <ImageIcon className="h-4 w-4" />
                  </Button>
                  <Button variant="outline" size="sm" className="h-11 shrink-0 gap-2 rounded-full px-4" onClick={() => setEditOpen(true)}>
                    <Settings2 className="h-4 w-4" /><span className="hidden xs:inline">Edit profile</span><span className="xs:hidden">Edit</span>
                  </Button>
                </>
              )}

            </div>
          </div>

          <div className="mt-3">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="font-display text-[22px] font-bold leading-tight tracking-tight sm:text-2xl">{displayName}</h1>
              <AccountBadge accountType={profile.account_type} verified={profile.verified} size="sm" />
            </div>
            <p className="text-sm text-muted-foreground">@{profile.username}</p>

            {isOfficial(profile.account_type) && (() => {
              const meta = ACCOUNT_META[profile.account_type as Exclude<AccountType, "citizen">];
              return (
                <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
                  <span className="inline-flex items-center gap-1.5 font-medium text-foreground">
                    <meta.icon className="h-3.5 w-3.5" /> {meta.label}
                  </span>
                  {profile.office && <span>· {profile.office}</span>}
                  {profile.jurisdiction && <span>· {profile.jurisdiction}</span>}
                  {profile.agency && <span>· {profile.agency}</span>}
                  {profile.official_website && (
                    <a href={profile.official_website} target="_blank" rel="noreferrer" className="text-primary hover:underline">Official site</a>
                  )}
                </div>
              );
            })()}

            {profile.bio && <p className="mt-3 max-w-2xl text-[15px] leading-relaxed">{profile.bio}</p>}

            <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
              {locText && <span className="flex items-center gap-1.5"><MapPin className="h-4 w-4" />{locText}</span>}
              {profile.website && (
                <a href={profile.website} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 text-primary hover:underline">
                  <Globe className="h-4 w-4" />{profile.website.replace(/^https?:\/\//, "")}
                </a>
              )}
              <span className="flex items-center gap-1.5"><Calendar className="h-4 w-4" />Joined {format(new Date(profile.created_at), "MMMM yyyy")}</span>
            </div>

            {/* What drives me — the person's own words about why they're here */}
            {(profile.interests?.length ?? 0) > 0 || communities.length > 0 ? (
              <div className="panel mt-4 flex flex-wrap items-center justify-between gap-4 px-4 py-3.5">
                {(profile.interests?.length ?? 0) > 0 && (
                  <div className="min-w-0">
                    <div className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground">What drives me</div>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {profile.interests!.slice(0, 6).map((tag, i) => (
                        <span
                          key={tag}
                          className="inline-flex items-center gap-1.5 rounded-full border border-border bg-surface px-3 py-1 text-[13px]"
                        >
                          <span className={cn("h-1.5 w-1.5 rounded-full", DRIVE_DOTS[i % DRIVE_DOTS.length])} />
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {communities.length > 0 && (
                  <div className="flex items-center gap-3">
                    <span className="text-[13px] text-muted-foreground">Member of</span>
                    <span className="flex -space-x-2">
                      {communities.slice(0, 3).map((c: any) => (
                        <Avatar key={c.id} className="h-8 w-8 border-2 border-background">
                          <AvatarImage src={c.cover_url ?? undefined} />
                          <AvatarFallback className="bg-surface text-[11px]">{(c.name ?? "?")[0]}</AvatarFallback>
                        </Avatar>
                      ))}
                    </span>
                    {communities.length > 3 && (
                      <span className="text-[13px] text-muted-foreground">+{communities.length - 3}</span>
                    )}
                  </div>
                )}
              </div>
            ) : null}

            {/* Relationships — typographic, tappable, no dashboard cards */}
            <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-[15px]">
              <Link
                to="/followers/$username"
                params={{ username: profile.username }}
                className="hover:underline"
              >
                <b className="font-semibold tabular-nums">{nf(metrics.followers)}</b>{" "}
                <span className="text-muted-foreground">{metrics.followers === 1 ? "Follower" : "Followers"}</span>
              </Link>
              <Link
                to="/following/$username"
                params={{ username: profile.username }}
                className="hover:underline"
              >
                <b className="font-semibold tabular-nums">{nf(metrics.following)}</b>{" "}
                <span className="text-muted-foreground">Following</span>
              </Link>
              <span>
                <b className="font-semibold tabular-nums">{nf(metrics.communitiesJoined)}</b>{" "}
                <span className="text-muted-foreground">
                  {metrics.communitiesJoined === 1 ? "Community" : "Communities"}
                </span>
              </span>
            </div>
          </div>

        </div>

        {/* Tabs */}
        <Tabs defaultValue="posts" className="mt-5">
          <div className="sticky top-14 z-20 max-w-full overflow-hidden border-b border-border bg-background/90 backdrop-blur md:top-0">
            <TabsList className="no-scrollbar h-auto w-full max-w-full snap-x snap-mandatory justify-start gap-0 overflow-x-auto overscroll-x-contain rounded-none border-0 bg-transparent p-0 px-1 sm:px-4">
              {tabsShown.map(([v, l]) => (
                <TabsTrigger
                  key={v}
                  value={v}
                  className="relative min-h-11 shrink-0 snap-start whitespace-nowrap rounded-none border-0 bg-transparent px-3.5 py-3 text-[15px] font-medium text-muted-foreground shadow-none hover:text-foreground data-[state=active]:bg-transparent data-[state=active]:font-semibold data-[state=active]:text-foreground data-[state=active]:shadow-none after:absolute after:inset-x-3 after:bottom-0 after:h-[3px] after:rounded-full after:bg-transparent data-[state=active]:after:bg-primary sm:px-4 sm:py-3.5"
                >
                  {l}
                </TabsTrigger>
              ))}
            </TabsList>
          </div>



          <TabsContent value="posts" className="mt-0">
            {posts.length === 0 ? <Empty>No posts yet.</Empty> : (
              <div className="divide-y divide-border">{posts.map(p => <div key={p.id} className="px-4 py-3 sm:px-6"><PostCard post={p} /></div>)}</div>
            )}
            {activity.length > 0 && (
              <div className="border-t border-border px-4 py-5 sm:px-6">
                <SectionTitle>Recent activity</SectionTitle>
                <ActivityFeed items={activity} name={displayName} avatar={profile.avatar_url} initials={initials} />
              </div>
            )}
          </TabsContent>

          <TabsContent value="replies" className="mt-0">
            {replies.length === 0 ? <Empty>No replies yet.</Empty> : (
              <ul className="divide-y divide-border">
                {replies.map((r: any) => (
                  <li key={r.id} className="px-4 py-4 sm:px-6">
                    <div className="flex gap-3">
                      <Avatar className="h-9 w-9 shrink-0">
                        <AvatarImage src={profile.avatar_url ?? undefined} />
                        <AvatarFallback className="text-xs">{initials}</AvatarFallback>
                      </Avatar>
                      <div className="min-w-0 flex-1">
                        <div className="text-[13px] text-muted-foreground">
                          Replied to{" "}
                          {r.posts?.id ? (
                            <Link to="/posts/$id" params={{ id: r.posts.id }} className="font-medium text-foreground hover:underline">{r.posts.title}</Link>
                          ) : <span className="font-medium text-foreground">a post</span>}
                          {" · "}{formatDistanceToNow(new Date(r.created_at), { addSuffix: true })}
                        </div>
                        <p className="mt-1 text-[15px] leading-relaxed">{r.content}</p>
                        {r.is_solution && (
                          <span className="mt-2 inline-flex items-center gap-1.5 rounded-full border border-border px-2.5 py-0.5 text-xs font-medium text-success">
                            <CheckCircle2 className="h-3.5 w-3.5" /> Accepted solution
                          </span>
                        )}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </TabsContent>

          <TabsContent value="reports" className="mt-0">
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 border-b border-border px-4 py-4 text-sm sm:px-6">
              <Count value={metrics.issuesReported} label="Reported" />
              <Count value={metrics.issuesResolved} label="Resolved" />
              <Count value={metrics.verificationsGiven} label="Verifications given" />
            </div>
            {reports.length === 0 ? <Empty>No reports yet.</Empty> : (
              <div className="divide-y divide-border">{reports.map(p => <div key={p.id} className="px-4 py-3 sm:px-6"><PostCard post={p} /></div>)}</div>
            )}
          </TabsContent>

          <TabsContent value="solutions" className="mt-0">
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 border-b border-border px-4 py-4 text-sm sm:px-6">
              <Count value={metrics.acceptedSolutions} label="Accepted solutions" />
              <Count value={metrics.comments} label="Contributions" />
            </div>
            {(() => {
              const sols = acceptedReplies;
              if (sols.length === 0) return <Empty>No accepted solutions yet.</Empty>;
              return (
                <ul className="divide-y divide-border">
                  {sols.map((r: any) => (
                    <li key={r.id} className="px-4 py-4 sm:px-6">
                      <div className="flex items-center gap-2 text-[13px] text-muted-foreground">
                        <CheckCircle2 className="h-4 w-4 text-success" />
                        Solution on{" "}
                        {r.posts?.id ? <Link to="/posts/$id" params={{ id: r.posts.id }} className="font-medium text-foreground hover:underline">{r.posts.title}</Link> : "a report"}
                      </div>
                      <p className="mt-1.5 text-[15px] leading-relaxed">{r.content}</p>
                    </li>
                  ))}
                </ul>
              );
            })()}
          </TabsContent>

          {showsImpact && (
            <TabsContent value="impact" className="mt-0">
              <OfficialImpactSection ownerId={profile.id} isOwner={isMe} />
            </TabsContent>
          )}



          <TabsContent value="media" className="mt-0 px-4 py-4 sm:px-6">
            {isMe && (
              <div className="mb-3 flex justify-end">
                <Button size="sm" variant="outline" className="rounded-full" onClick={() => setGalleryOpen(true)}>
                  <ImageIcon className="h-4 w-4" /> Manage gallery
                </Button>
              </div>
            )}
            {mediaItems.length === 0 ? <Empty>No media yet.</Empty> : (
              <div className="grid grid-cols-3 gap-0.5 sm:gap-1">
                {mediaItems.map((m, i) => (
                  <button
                    key={m.id}
                    type="button"
                    onClick={() => setLightbox({ open: true, start: i, items: mediaItems })}
                    className="group relative aspect-square overflow-hidden bg-muted"
                  >
                    {m.media_type === "video" ? (
                      <>
                        <video src={m.url} muted playsInline preload="metadata" className="h-full w-full object-cover" />
                        <span className="pointer-events-none absolute right-2 top-2 text-background drop-shadow"><Play className="h-4 w-4 fill-current" /></span>
                      </>
                    ) : (
                      <img src={m.url} alt="" loading="lazy" className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.03]" />
                    )}
                  </button>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="communities" className="mt-0">
            {communities.length === 0 ? <Empty>No communities joined yet.</Empty> : (
              <ul className="divide-y divide-border">
                {communities.map((c: any) => (
                  <li key={c.id}>
                    <Link to="/communities/$slug" params={{ slug: c.slug }} className="flex items-center gap-3 px-4 py-3.5 transition-colors hover:bg-muted/50 sm:px-6">
                      <Avatar className="h-11 w-11 rounded-lg">
                        <AvatarImage src={c.cover_url ?? undefined} />
                        <AvatarFallback className="rounded-lg bg-muted"><UsersIcon className="h-5 w-5 text-muted-foreground" /></AvatarFallback>
                      </Avatar>
                      <div className="min-w-0 flex-1">
                        <div className="truncate font-semibold">{c.name}</div>
                        <div className="text-[13px] text-muted-foreground">{c.member_count ?? 0} members · {c.role}</div>
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
            {projects.length > 0 && (
              <div className="border-t border-border px-4 py-5 sm:px-6">
                <SectionTitle>Projects</SectionTitle>
                <ul className="divide-y divide-border">
                  {projects.map((p: any, i: number) => (
                    <li key={p.id + i}>
                      <Link to="/projects/$id" params={{ id: p.id }} className="flex items-center gap-3 py-3">
                        <Briefcase className="h-4 w-4 text-muted-foreground" />
                        <div className="min-w-0 flex-1">
                          <div className="truncate text-sm font-medium">{p.title}</div>
                          <div className="text-xs capitalize text-muted-foreground">{p.status} · {p.category}</div>
                        </div>
                        {p.owned && <Badge variant="secondary" className="text-[10px]">Organizer</Badge>}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </TabsContent>

          <TabsContent value="about" className="mt-0 px-4 py-5 sm:px-6">
            <div className="space-y-6">
              <div className="panel px-4 py-4">
                <SectionTitle>Impact radar</SectionTitle>
                <p className="-mt-1 mb-2 text-xs text-muted-foreground">Where this member's contribution is strongest.</p>
                <ImpactRadar
                  axes={liveScore.radar}
                />
              </div>

              <div>
                <SectionTitle>Score over time</SectionTitle>
                <p className="-mt-1 mb-2 text-xs text-muted-foreground">
                  Compare recent momentum with long-term impact.
                </p>
                <ScoreWindows userId={profile.id} />
              </div>

              <div>

                <SectionTitle>How this is calculated</SectionTitle>
                <p className="-mt-1 mb-2 text-xs text-muted-foreground">
                  Every number below comes from real activity — posts, votes, answers, spaces and projects. Tap a row to see the maths.
                </p>
                <ScoreBreakdown metrics={civicInputs} badges={badgeList} />
              </div>

              <div>
                <SectionTitle>Reputation</SectionTitle>
                <div className="divide-y divide-border">
                  <MetricRow icon={ShieldCheck} label="Trust" value={`${trust}/100`} progress={trust} />
                  <MetricRow icon={Trophy} label="Civic score" value={liveScore.civic} progress={liveScore.civic} />
                  <MetricRow icon={BarChart3} label="Impact" value={liveScore.impact} progress={liveScore.impact} />
                  <MetricRow icon={Heart} label="Reputation points" value={profile.reputation_points} />
                </div>
              </div>

              <div>
                <SectionTitle>Contribution</SectionTitle>
                <div className="divide-y divide-border">
                  <MetricRow icon={FileText} label="Posts" value={metrics.posts} />
                  <MetricRow icon={MessageCircle} label="Comments" value={metrics.comments} />
                  <MetricRow icon={CheckCircle2} label="Accepted solutions" value={metrics.acceptedSolutions} />
                  <MetricRow icon={ThumbsUp} label="Support received" value={metrics.supportReceived} />
                  <MetricRow icon={Bookmark} label="Bookmarks received" value={metrics.bookmarksReceived} />
                  <MetricRow icon={Eye} label="Total reach" value={metrics.totalReach.toLocaleString()} />
                </div>
              </div>

              <div>
                <SectionTitle>My Journey</SectionTitle>
                <JourneyTimeline
                  milestones={buildJourney({
                    joinedAt: profile.created_at,
                    issuesReported: metrics.issuesReported,
                    verificationsGiven: metrics.verificationsGiven,
                    acceptedSolutions: metrics.acceptedSolutions,
                    projects: metrics.projectsJoined + metrics.projectsCreated,
                    issuesResolved: metrics.issuesResolved,
                    spaces: metrics.communitiesJoined,
                  })}
                />
              </div>

              <div>
                <SectionTitle>Badges</SectionTitle>
                <BadgeGrid
                  badges={buildBadges({
                    issuesReported: metrics.issuesReported,
                    verificationsGiven: metrics.verificationsGiven,
                    followers: metrics.followers,
                    acceptedSolutions: metrics.acceptedSolutions,
                    spaces: metrics.communitiesJoined,
                    issuesResolved: metrics.issuesResolved,
                  })}
                />
                {achievements.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {achievements.map((a) => a.achievements && (
                      <span key={a.achievements.id} className="inline-flex items-center gap-2 rounded-full border border-border px-3 py-1.5 text-[13px]">
                        <span aria-hidden>{a.achievements.icon || "•"}</span>
                        <span className="font-medium">{a.achievements.name}</span>
                      </span>
                    ))}
                  </div>
                )}
              </div>


              <div className="flex flex-wrap gap-3">
                <Link
                  to="/followers/$username"
                  params={{ username: profile.username }}
                  className="inline-flex min-h-11 items-center rounded-full border border-border px-4 text-sm font-medium hover:bg-muted"
                >
                  {nf(metrics.followers)} followers
                </Link>
                <Link
                  to="/following/$username"
                  params={{ username: profile.username }}
                  className="inline-flex min-h-11 items-center rounded-full border border-border px-4 text-sm font-medium hover:bg-muted"
                >
                  {nf(metrics.following)} following
                </Link>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      <MediaLightbox media={lightbox.items} startIndex={lightbox.start} open={lightbox.open} onOpenChange={(o) => setLightbox(s => ({ ...s, open: o }))} />
      {isMe && (
        <>
          <EditProfileDialog
            open={editOpen}
            onOpenChange={setEditOpen}
            profile={profile}
            onSaved={(patch) => setProfile((p) => p ? { ...p, ...patch } as ProfileData : p)}
          />
          <ProfileGalleryDialog
            open={galleryOpen}
            onOpenChange={setGalleryOpen}
            userId={profile.id}
            onChanged={() => loadGallery(profile.id)}
          />
          <ProfilePhotoDialog
            open={photoOpen}
            onOpenChange={setPhotoOpen}
            currentUrl={profile.avatar_url}
            onSaved={(url) => setProfile((p) => (p ? { ...p, avatar_url: url } : p))}
          />
        </>
      )}
    </AppShell>
  );
}

/* ---------- pieces ---------- */

function Count({ value, label }: { value: number; label: string }) {
  return (
    <span className="text-[15px]">
      <b className="font-semibold tabular-nums">{typeof value === "number" ? value.toLocaleString() : value}</b>{" "}
      <span className="text-muted-foreground">{label}</span>
    </span>
  );
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return <h2 className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">{children}</h2>;
}

function Empty({ children }: { children: React.ReactNode }) {
  return <div className="px-6 py-16 text-center text-sm text-muted-foreground">{children}</div>;
}

function MetricRow({ icon: Icon, label, value, progress }: { icon: any; label: string; value: React.ReactNode; progress?: number }) {
  return (
    <div className="flex items-center gap-3 py-3">
      <Icon className="h-4 w-4 shrink-0 text-muted-foreground" />
      <span className="flex-1 text-sm">{label}</span>
      {typeof progress === "number" && (
        <span className="hidden h-1 w-24 overflow-hidden rounded-full bg-muted sm:block">
          <span className="block h-full rounded-full bg-foreground" style={{ width: `${Math.max(0, Math.min(100, progress))}%` }} />
        </span>
      )}
      <span className="text-sm font-semibold tabular-nums">{value}</span>
    </div>
  );
}

const ACTIVITY_ICON: Record<string, any> = {
  post: Flag, report: Flag, comment: MessageCircle, verify: ShieldCheck,
  resolve: CheckCircle2, follow: UsersIcon, join: UsersIcon, vote: ThumbsUp, save: Bookmark,
};

function activityIcon(action: string) {
  const key = Object.keys(ACTIVITY_ICON).find(k => action.includes(k));
  return key ? ACTIVITY_ICON[key] : MessageCircle;
}

function activityText(name: string, a: any) {
  const what = String(a.action || "").replace(/_/g, " ");
  const target = a.entity_type ? ` ${a.entity_type}` : "";
  return `${name} ${what}${target}`.trim();
}

function ActivityFeed({ items, name, avatar, initials }: { items: any[]; name: string; avatar: string | null; initials: string }) {
  return (
    <ul className="divide-y divide-border">
      {items.slice(0, 12).map((a) => {
        const Icon = activityIcon(String(a.action || ""));
        const place = a.metadata?.location_name || a.metadata?.community || null;
        return (
          <li key={a.id} className="flex items-start gap-3 py-3">
            <span className="relative shrink-0">
              <Avatar className="h-9 w-9">
                <AvatarImage src={avatar ?? undefined} />
                <AvatarFallback className="text-xs">{initials}</AvatarFallback>
              </Avatar>
              <span className="absolute -bottom-1 -right-1 grid h-5 w-5 place-items-center rounded-full border border-border bg-background">
                <Icon className="h-3 w-3 text-muted-foreground" />
              </span>
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-sm first-letter:uppercase">{activityText(name, a)}</p>
              <p className="text-[13px] text-muted-foreground">
                {place ? `${place} · ` : ""}{formatDistanceToNow(new Date(a.created_at), { addSuffix: true })}
              </p>
            </div>
          </li>
        );
      })}
    </ul>
  );
}

function ProfileRail({
  profile,
  metrics,
  communities,
  projects,
}: {
  profile: ProfileData;
  metrics: Metrics;
  communities: any[];
  projects: any[];
}) {
  const locText = profile.city && profile.country ? `${profile.city}, ${profile.country}` : profile.location;
  const first = (profile.full_name || profile.username).split(" ")[0];

  return (
    <div className="space-y-4 py-3">
      <section className="panel px-4 py-4">
        <h3 className="font-display text-[15px] font-semibold">About {first}</h3>
        {profile.bio ? (
          <p className="mt-2 text-[13px] leading-relaxed text-muted-foreground">{profile.bio}</p>
        ) : (
          <p className="mt-2 text-[13px] text-muted-foreground">No bio yet.</p>
        )}
        <ul className="mt-3 space-y-2 text-[13px] text-muted-foreground">
          {locText && <li className="flex items-center gap-2"><MapPin className="h-3.5 w-3.5" />{locText}</li>}
          <li className="flex items-center gap-2">
            <Calendar className="h-3.5 w-3.5" />Joined {format(new Date(profile.created_at), "MMMM yyyy")}
          </li>
          {profile.website && (
            <li className="flex items-center gap-2">
              <Globe className="h-3.5 w-3.5" />
              <a href={profile.website} target="_blank" rel="noreferrer" className="truncate hover:underline">
                {profile.website.replace(/^https?:\/\//, "")}
              </a>
            </li>
          )}
        </ul>
      </section>

      <section className="panel px-4 py-4">
        <div className="flex items-center justify-between">
          <h3 className="font-display text-[15px] font-semibold">Communities</h3>
          <Link to="/communities" className="text-xs text-muted-foreground hover:underline">Browse</Link>
        </div>
        {communities.length === 0 ? (
          <p className="mt-2 text-[13px] text-muted-foreground">Not in a community yet.</p>
        ) : (
          <ul className="mt-2 divide-y divide-border">
            {communities.slice(0, 4).map((c: any) => (
              <li key={c.id}>
                <Link to="/communities/$slug" params={{ slug: c.slug }} className="flex items-center gap-3 py-2.5">
                  <Avatar className="h-9 w-9 rounded-xl">
                    <AvatarImage src={c.cover_url ?? undefined} alt="" />
                    <AvatarFallback className="rounded-xl bg-muted"><UsersIcon className="h-4 w-4 text-muted-foreground" /></AvatarFallback>
                  </Avatar>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium">{c.name}</div>
                    <div className="text-xs text-muted-foreground">{nf(c.member_count ?? 0)} members</div>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>

      {projects.length > 0 && (
        <section className="panel px-4 py-4">
          <h3 className="font-display text-[15px] font-semibold">{first} is contributing to</h3>
          <ul className="mt-2 divide-y divide-border">
            {projects.slice(0, 4).map((pr: any, i: number) => (
              <li key={String(pr.id) + i}>
                <Link to="/projects/$id" params={{ id: pr.id }} className="flex items-center gap-3 py-2.5">
                  <Briefcase className="h-4 w-4 shrink-0 text-muted-foreground" />
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium">{pr.title}</div>
                    <div className="text-xs capitalize text-muted-foreground">{pr.status}</div>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className="panel px-4 py-4">
        <h3 className="font-display text-[15px] font-semibold">Reach</h3>
        <p className="mt-1 text-[13px] text-muted-foreground">
          {nf(metrics.totalReach)} people have seen what {first} shared.
        </p>
      </section>
    </div>
  );
}

function levelTitle(level: number) {
  if (level >= 6) return "Changemaker";
  if (level >= 5) return "Community Builder";
  if (level >= 4) return "Problem Solver";
  if (level >= 3) return "Connector";
  if (level >= 2) return "Verifier";
  return "Reporter";
}

